import React, { useState, useEffect } from "react";
import StepOne from "./components/StepOne.js";
import StepTwo from "./components/StepTwo";
import { downloadCSV } from "./services/csvService";
import { Helmet } from "react-helmet-async";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider as MuiDivider,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { spacing } from "@mui/system";
import { CheckIcon } from "lucide-react";

const Divider = styled(MuiDivider)(spacing);

// Main App Component
const PreventivoExpress = () => {
  // --- STATE MANAGEMENT ---
  const [step, setStep] = useState(1); // 3 = Success

  // Step 1 Data
  const [plate, setPlate] = useState("");
  const [phone, setPhone] = useState("");

  // Step 2 Data
  const [addressComponents, setAddressComponents] = useState(null);
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState("basic");
  const [guarantees, setGuarantees] = useState([]);
  const [privacy, setPrivacy] = useState(false);

  // Validation Errors
  const [errors, setErrors] = useState({});

  // Admin / Data Persistence
  const [leads, setLeads] = useState([]);

  // Load existing leads from localStorage on mount
  useEffect(() => {
    const savedLeads = localStorage.getItem("rca_lead_requests");
    if (savedLeads) {
      setLeads(JSON.parse(savedLeads));
    }
  }, []);

  // --- ACTIONS ---

  const handleNextStep1 = () => {
    const newErrors = {};

    // Basic Plate Validation (Simple pattern check for Italy: AA000AA or similar length)
    if (!plate || plate.length < 5) {
      newErrors.plate = "Inserisci una targa valida (es. AB123CD)";
    }

    // Basic Phone Validation
    if (!phone || phone.length < 9) {
      newErrors.phone = "Inserisci un numero di cellulare valido";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setStep(2);
    // Scroll to top
    window.scrollTo(0, 0);
  };

  const toggleGuarantee = (g) => {
    setGuarantees((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Validate Address
    // Allow manual entry (length > 3) to accommodate failed API loads
    if (
      !addressComponents ||
      !addressComponents.full_address ||
      addressComponents.full_address.trim().length < 4
    ) {
      newErrors.address = "Inserisci un indirizzo valido.";
    }

    // Validate Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      newErrors.email = "Inserisci una email valida.";
    }

    // Validate Privacy
    if (!privacy) {
      newErrors.privacy = "Il consenso alla privacy è obbligatorio.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // --- SAVE LOGIC ---
    const newLead = {
      id: crypto.randomUUID(),
      /* timestamp: new Date().toISOString(), */
      plate,
      phone,
      /* address_components: addressComponents, */
      email,
      /* request_type: requestType,
      guarantees: requestType === "extended" ? guarantees : [],
      privacy_accepted: privacy,
      source_qr: "QR_DEFAULT", */
    };

    const updatedLeads = [...leads, newLead];
    setLeads(updatedLeads);
    localStorage.setItem("rca_lead_requests", JSON.stringify(updatedLeads));

    try {
      const response = await fetch(
        "https://h.albato.com/wh/38/1lf9ns1/VdOzTQV-3LWRYNLTY85zCalJ_BfsbRn3zvx-sBjjO8I/",
        {
          method: "POST",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to send lead to Albato");
      }
    } catch (error) {
      console.error("Error sending lead to Albato:", error);
    }
    // Move to Success Screen
    /* setStep(3); */
  };

  const resetForm = () => {
    setPlate("");
    setPhone("");
    setAddressComponents(null);
    setEmail("");
    setRequestType("basic");
    setGuarantees([]);
    setPrivacy(false);
    setErrors({});
    setStep(1);
  };

  // --- RENDER ---
  return (
    <>
      <Helmet title="Preventivo Express" />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h3" display="inline">
          Preventivo Express
        </Typography>
      </Box>
      <Divider my={4} />
      <Card sx={{ marginBottom: "16px", padding: "8px" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: "calc(100vh-64px)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                width: "full",
                backgroundColor: "white",
                boxShadow: "sm",
                padding: "4",
                position: "sticky",
                top: "0",
                zIndex: "50",
              }}
            >
              <Box
                sx={{
                  maxWidth: "md",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* Admin CSV Download (hidden in plain sight for demo) */}
                {leads.length > 0 && (
                  <button
                    onClick={() => downloadCSV(leads)}
                    className="text-xs text-gray-400 hover:text-blue-600 underline"
                  >
                    Admin CSV ({leads.length})
                  </button>
                )}
              </Box>
            </Box>

            {/* Main Content Area */}
            <Box
              sx={{
                width: "full",
                maxWidth: "md",

                minHeight: "calc(100vh-64px)",
                boxShadow: "lg",
                padding: "6",
                my: "6",
                borderRadius: "xl",
                minHeight: "0",
              }}
            >
              {/* Progress Bar */}
              {step < 3 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  px={2}
                  mb={6}
                >
                  <Box
                    sx={{
                      height: "10px",
                      width: "50%",
                      borderRadius: "5px",
                      backgroundColor: step >= 1 ? "blue" : "gray",
                    }}
                  ></Box>
                  <Box
                    sx={{
                      height: "10px",
                      width: "50%",
                      borderRadius: "5px",
                      backgroundColor: step >= 2 ? "blue" : "gray",
                    }}
                  ></Box>
                </Box>
              )}

              {step === 1 && (
                <StepOne
                  plate={plate}
                  phone={phone}
                  setPlate={setPlate}
                  setPhone={setPhone}
                  onNext={handleNextStep1}
                  errors={errors}
                />
              )}

              {step === 2 && (
                <StepTwo
                  addressComponents={addressComponents}
                  setAddressComponents={setAddressComponents}
                  email={email}
                  setEmail={setEmail}
                  requestType={requestType}
                  setRequestType={setRequestType}
                  guarantees={guarantees}
                  toggleGuarantee={toggleGuarantee}
                  privacy={privacy}
                  setPrivacy={setPrivacy}
                  onSubmit={handleSubmit}
                  onBack={() => setStep(1)}
                  errors={errors}
                />
              )}

              {step === 3 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    animation: "fadeIn 0.5s ease-in-out",
                  }}
                >
                  {/* Cerchio con l’icona */}
                  <Avatar
                    sx={{
                      bgcolor: "#376fd0",
                      width: 80,
                      height: 80,
                      margin: "0 auto",
                      mb: 3,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 40, color: "green.600" }} />
                  </Avatar>

                  {/* Titolo */}
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="text.primary"
                    mb={1}
                  >
                    Richiesta Inviata!
                  </Typography>

                  {/* Testo descrittivo */}
                  <Typography variant="body1" color="text.secondary" mb={4}>
                    La tua richiesta di preventivo è stata registrata
                    correttamente. Verrai ricontattato al più presto da un
                    nostro consulente.
                  </Typography>

                  {/* Bottone */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={resetForm}
                  >
                    Nuova Richiesta
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default PreventivoExpress;
